import { IressAlert, IressCheckbox, IressPanel, IressStack, IressTable, TableColumn } from '@iress-oss/ids-components';
import { FC, useState } from 'react';
import { RetirementFund } from '@xplan-pension-switching/common';

import '../../../styles/PlanSelectionModal.css';

export type RetirementFundRow = RetirementFund & { isSelected: boolean };

const useGetTableRows = (
    plans: Array<RetirementFund & { isSelected: boolean }>,
    onSelectionChanged: (retirementFundIds: number[]) => void
): RetirementFundRow[] => {
    const [rows, setRows] = useState<RetirementFundRow[]>(plans);

    const onChange = (id: number) => {
        const mappedRows = rows.map(r => (r.id === id ? { ...r, isSelected: !r.isSelected } : r));
        const filteredRows = mappedRows.filter(r => r.isSelected).map(r => r.id);
        setRows(mappedRows);
        onSelectionChanged(filteredRows);
    };

    return rows.map((row: RetirementFundRow) => ({
        ...row,
        isSelectedCell: (
            <IressCheckbox
                data-testid={`plan-select_${row?.id}`}
                value={row?.id}
                checked={row?.isSelected}
                onChange={() => onChange(row!.id)}
            >
                <span className="iress-sr-only">Select {row?.name}</span>
            </IressCheckbox>
        ),
    }));
};

export const PlanSelection: FC<{
    plans: Array<RetirementFund & { isSelected: boolean }>;
    onSelectionChanged: (retirementFundIds: number[]) => void;
    noFundsError: boolean;
}> = props => {
    const { plans, onSelectionChanged, noFundsError } = props;
    const rows = useGetTableRows(plans, onSelectionChanged);

    const columnDefs: TableColumn<RetirementFundRow>[] = [
        {
            key: 'isSelectedCell',
            label: '',
            divider: false,
            width: '5%',
            align: 'center',
        },
        {
            key: 'name',
            label: 'Plan',
            divider: false,
            width: '45%',
        },
        {
            key: 'type',
            label: 'Type',
            divider: false,
            width: '30%',
        },
        {
            key: 'fundValue',
            label: 'Value (GBP)',
            divider: false,
            width: '20%',
            format: 'currency',
            currencyCode: '',
            align: 'right',
        },
    ];
    return (
        <IressStack gutter={'lg'}>
            <div aria-live="polite" aria-relevant="additions">
                <IressStack gutter={'lg'}>
                    <IressAlert className={'info'} status={'info'}>
                        You cannot change selected plans once a scenario is created.
                    </IressAlert>
                    {noFundsError && (
                        <IressAlert status={'danger'} heading={<h3>No plans selected</h3>}>
                            <p>Select at least one plan to continue.</p>
                        </IressAlert>
                    )}
                </IressStack>
            </div>
            <IressPanel>
                <IressTable
                    data-testid={'plan-selection-table'}
                    className={'plan-selection-table xps-table'}
                    caption={'Select plans'}
                    hiddenCaption
                    columns={columnDefs}
                    rows={rows}
                    scope="col"
                />
            </IressPanel>
        </IressStack>
    );
};
